import apiClient, { extractData, handleApiError, tokenManager } from './api.js';

export class UserService {
  /**
   * Check if a user exists by phone number
   * GET /api/v1/users/check-phone?phone=...
   * @param {string} phone
   * @returns {Promise<{exists: boolean, return_value: string|null}>}
   */
  static async checkUserExists(phone) {
    try {
      const response = await apiClient.get('/users/check-phone', {
        params: { phone },
      });
      return extractData(response);
    } catch (error) {
      console.error('Error checking user existence:', error);
      throw new Error(handleApiError(error));
    }
  }

  /**
   * Login - authenticate user with phone + PIN
   * POST /api/v1/auth/login
   * @param {string} phone
   * @param {string} pin
   * @returns {Promise<{user: Object, access_token: string, refresh_token: string, expires_in: number}>}
   */
  static async login(phone, pin) {
    try {
      const response = await apiClient.post('/auth/login', { phone, pin });
      const data = extractData(response);

      // Store JWT tokens
      tokenManager.setTokens({
        access_token: data.access_token,
        refresh_token: data.refresh_token,
        expires_in: data.expires_in,
      });

      return data;
    } catch (error) {
      const message = handleApiError(error);
      // Only log unexpected errors (not 400/401 auth failures)
      const status = error.response?.status;
      if (status !== 400 && status !== 401) {
        console.error('Error logging in:', error);
      }
      throw new Error(message);
    }
  }

  /**
   * Refresh access token
   * POST /api/v1/auth/refresh
   * @returns {Promise<{access_token: string, expires_in: number}>}
   */
  static async refreshToken() {
    try {
      const refreshToken = tokenManager.getRefreshToken();
      if (!refreshToken) throw new Error('No refresh token available');

      const response = await apiClient.post('/auth/refresh', {
        refresh_token: refreshToken,
      });
      const data = extractData(response);

      tokenManager.setTokens({
        access_token: data.access_token,
        expires_in: data.expires_in,
      });

      return data;
    } catch (error) {
      console.error('Error refreshing token:', error);
      throw new Error(handleApiError(error));
    }
  }

  /**
   * Logout - clear tokens (stateless JWT, server doesn't blacklist)
   * POST /api/v1/auth/logout
   */
  static async logout() {
    try {
      await apiClient.post('/auth/logout');
    } catch (error) {
      // Ignore errors - logout is mainly client-side
      console.warn('Logout API call failed (non-critical):', error);
    } finally {
      tokenManager.clearTokens();
    }
  }

  /**
   * Verify a user's PIN and return profile data
   * POST /api/v1/users/verify-pin
   * @param {string} phone
   * @param {string} pin
   * @returns {Promise<Object>}
   */
  static async verifyPin(phone, pin) {
    try {
      const response = await apiClient.post('/users/verify-pin', { phone, pin });
      return extractData(response);
    } catch (error) {
      console.error('Error verifying PIN:', error);
      throw new Error(handleApiError(error));
    }
  }

  /**
   * Create a new user (auto-login - returns tokens)
   * POST /api/v1/users
   * @param {Object} userData - { name, phone, pin, outlet_id, access_type }
   * @returns {Promise<{user: Object, access_token: string, refresh_token: string, expires_in: number}>}
   */
  static async createUser(userData) {
    try {
      const response = await apiClient.post('/users', {
        name: userData.name,
        phone: userData.phone,
        pin: userData.pin,
        outlet_id: userData.outlet_id || userData.outletId,
        access_type: userData.access_type || userData.accessType || 'owner',
      });
      const data = extractData(response);

      // Store JWT tokens (user is auto-logged in)
      if (data.access_token) {
        tokenManager.setTokens({
          access_token: data.access_token,
          refresh_token: data.refresh_token,
          expires_in: data.expires_in,
        });
      }

      return data;
    } catch (error) {
      console.error('Error creating user:', error);
      throw new Error(handleApiError(error));
    }
  }

  /**
   * Fetch user profile by user ID
   * GET /api/v1/users/{user_id}
   * @param {string} userId
   * @returns {Promise<Object>}
   */
  static async fetchUserProfile(userId) {
    try {
      const response = await apiClient.get(`/users/${userId}`);
      return extractData(response);
    } catch (error) {
      console.error('Error fetching user profile:', error);
      throw new Error(handleApiError(error));
    }
  }

  /**
   * Update user information
   * PATCH /api/v1/users/{user_id}
   * @param {string} userId
   * @param {Object} updateData - { name?, phone?, outlet_id?, upi_id?, access_type? }
   * @returns {Promise<Object>}
   */
  static async updateUser(userId, updateData) {
    try {
      const response = await apiClient.patch(`/users/${userId}`, updateData);
      return extractData(response);
    } catch (error) {
      console.error('Error updating user:', error);
      throw new Error(handleApiError(error));
    }
  }

  /**
   * Validate user credentials and return complete user data with tokens
   * This combines check-phone + login for backward compatibility
   * @param {string} phone
   * @param {string} pin
   * @returns {Promise<Object|null>} user object with tokens, or null on failure
   */
  static async validateUser(phone, pin) {
    try {
      console.log('[UserService] Logging in with phone:', phone);
      const data = await this.login(phone, pin);

      if (!data || !data.user) {
        console.log('[UserService] Login returned no user data');
        return null;
      }

      console.log('[UserService] Login successful. User:', data.user.name);

      // Return user data in the shape the frontend expects
      return {
        user_id: data.user.user_id,
        outlet_id: data.user.outlet_id,
        access_type: data.user.access_type,
        name: data.user.name,
        phone: data.user.phone,
        created_at: data.user.created_at,
        // tokens are already stored by login()
        access_token: data.access_token,
        refresh_token: data.refresh_token,
      };
    } catch (error) {
      console.error('Error validating user:', error);
      return null;
    }
  }

  /**
   * Fetch user's outlet ID
   * @param {string} userId
   * @returns {Promise<string|null>}
   */
  static async fetchUserOutletId(userId) {
    try {
      const user = await this.fetchUserProfile(userId);
      return user?.outlet_id || null;
    } catch (error) {
      console.error('Error fetching user outlet ID:', error);
      return null;
    }
  }
}
